import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type {
  CorrectionApprovalDomain,
  CorrectionApprovalView,
  CorrectionDesignationsDto,
  CorrectionReceiptLookupDto,
  CorrectionRegisterDto,
  CorrectionRequestsDto,
  CorrectionWorkflowMeDto,
  PaymentCorrectionStatus,
  SetCorrectionDesignationsInput,
} from '@nexao/contracts';
import {
  getCorrectionDesignations,
  getCorrectionWorkflowMe,
  listCorrectionApprovals,
  listCorrectionOverview,
  listCorrectionRegister,
  listCorrectionRequests,
  lookupCorrectionReceipt,
  setCorrectionDesignations,
  type CorrectionRegisterFilters,
} from '../services/correction-workflow.service';

/** Every query of the workflow hangs under one key, so a decision refreshes the list, the badge and the screen's own view together. */
export const CORRECTION_WORKFLOW_KEY = ['correction-workflow'] as const;

/** How often a selected verifier / approver's app asks "is anything waiting for me?" (desk, 2026-10-09: both stages must be told, not just see a count on the next page load). */
export const CORRECTION_ALERT_POLL_MS = 60_000;

export function useCorrectionWorkflowMe(): UseQueryResult<CorrectionWorkflowMeDto> {
  return useQuery({
    queryKey: [...CORRECTION_WORKFLOW_KEY, 'me'],
    queryFn: getCorrectionWorkflowMe,
    staleTime: 30_000,
    // Only someone who has been selected is asked again: everybody else's answer cannot change without them reloading, so they cost nothing.
    refetchInterval: (query) => (query.state.data?.isVerifier || query.state.data?.isApprover ? CORRECTION_ALERT_POLL_MS : false),
    refetchIntervalInBackground: true,
  });
}

export function useCorrectionDesignations(): UseQueryResult<CorrectionDesignationsDto> {
  return useQuery({ queryKey: [...CORRECTION_WORKFLOW_KEY, 'designations'], queryFn: getCorrectionDesignations });
}

export function useSaveCorrectionDesignations(): UseMutationResult<CorrectionDesignationsDto, unknown, SetCorrectionDesignationsInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setCorrectionDesignations,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CORRECTION_WORKFLOW_KEY }),
  });
}

export function useCorrectionRequests(query: { status?: PaymentCorrectionStatus; mine?: boolean }, enabled = true): UseQueryResult<CorrectionRequestsDto> {
  return useQuery({
    queryKey: [...CORRECTION_WORKFLOW_KEY, 'requests', query.status ?? 'all', query.mine ? 'mine' : 'everyone'],
    queryFn: () => listCorrectionRequests(query),
    enabled,
  });
}

/** The Modifications module's list: one domain, one view. Only fetched while the screen is open for a selected approver. */
export function useCorrectionApprovals(query: { domain: CorrectionApprovalDomain; view: CorrectionApprovalView }, enabled = true): UseQueryResult<CorrectionRequestsDto> {
  return useQuery({
    queryKey: [...CORRECTION_WORKFLOW_KEY, 'approvals', query.domain, query.view],
    queryFn: () => listCorrectionApprovals(query),
    enabled,
  });
}

/** The administrator's register: every request, every stage, read-only. Only fetched while that tab is open (the server answers 403 to anyone without the permission). */
export function useCorrectionOverview(enabled = true): UseQueryResult<CorrectionRequestsDto> {
  return useQuery({
    queryKey: [...CORRECTION_WORKFLOW_KEY, 'overview'],
    queryFn: () => listCorrectionOverview(),
    enabled,
  });
}

/** A receipt number the clerk typed, looked up on demand (a mutation: nothing is fetched until they press Find). */
export function useLookupCorrectionReceipt(): UseMutationResult<CorrectionReceiptLookupDto, unknown, string> {
  return useMutation({ mutationFn: lookupCorrectionReceipt });
}

/** The signed-in user's own requests — the Correction tab's register. Under the workflow key, so a new request or a decision refreshes it. */
export function useCorrectionRegister(filters: CorrectionRegisterFilters, enabled = true): UseQueryResult<CorrectionRegisterDto> {
  return useQuery({
    queryKey: [...CORRECTION_WORKFLOW_KEY, 'register', filters],
    queryFn: () => listCorrectionRegister(filters),
    enabled,
    staleTime: 0,
  });
}
